import clone from 'clone';

import {slInterface} from './sl-interface';
import SafeList from '../lib/safe-list';
import {OEAppInitResultCode, OEAppStatusErrorCode, OEAppStatusError, OEChangeTargetResult} from './oe-types';

export class OEInterface {

    constructor(moduleId) {
        this.moduleId = moduleId;

        this.preConnectCallbacks = new SafeList();
        this.connectCallbacks = new SafeList();
        this.releaseCallbacks = new SafeList();

        //
        this.onInitializationBegin = this.onInitializationBegin.bind(this);
        this.onReadyForLicensingService = this.onReadyForLicensingService.bind(this);
        this.onInitializationFinished = this.onInitializationFinished.bind(this);
        this.onLicensingModuleConnected = this.onLicensingModuleConnected.bind(this);

        //
        this.targetChangeCallbacks = new SafeList((u, v) => u.target == v.target && u.fn == v.fn);

        //
        this.reset();
    }

    reset() {

        if(this.sharedNotificationCenter)   {
            if(this.sharedNotificationCenter.isRegistered(this.NotificationName.initializationBegin, this.onInitializationBegin))   this.sharedNotificationCenter.unregister(this.NotificationName.initializationBegin, this.onInitializationBegin);
            if(this.sharedNotificationCenter.isRegistered(this.NotificationName.readyForLicensingService, this.onReadyForLicensingService))   this.sharedNotificationCenter.unregister(this.NotificationName.readyForLicensingService, this.onReadyForLicensingService);
            if(this.sharedNotificationCenter.isRegistered(this.NotificationName.initializationFinished, this.onInitializationFinished))   this.sharedNotificationCenter.unregister(this.NotificationName.initializationFinished, this.onInitializationFinished);
        }

        // we can not make sure that the content window of the embeded iframe loading the core module does not leak at this time
        // but at least we can prevent the leakage of the big array buffers of the core module by setting all references to null explicitely
        if(this.element)    {
            this.element.contentWindow.buffer = null;
            this.element.contentWindow.HEAP8 = null;
            this.element.contentWindow.HEAP16 = null;
            this.element.contentWindow.HEAP32 = null;
            this.element.contentWindow.HEAPF32 = null;
            this.element.contentWindow.HEAPF64 = null;
            this.element.contentWindow.HEAPU8 = null;
            this.element.contentWindow.HEAPU16 = null;
            this.element.contentWindow.HEAPU32 = null;
            this.element.contentWindow.wasmMemory = null;
            this.element.contentWindow.memory = null;
            this.element.contentWindow.asmLibraryArg = null;
            this.element.contentWindow.wasmTable = null;
            this.element.contentWindow.asm = null;
            this.element.contentWindow.FS = null;
        }

        if(this.Module) {
            this.Module.HEAP8 = null;
            this.Module.HEAP16 = null;
            this.Module.HEAP32 = null;
            this.Module.HEAPF32 = null;
            this.Module.HEAPF64 = null;
            this.Module.HEAPU8 = null;
            this.Module.HEAPU16 = null;
            this.Module.HEAPU32 = null;
            this.Module.asm = null;
        }

        //
        this.preReady = false;
        this.readyForLicensingService = false;
        this.ready = false;
        this.initializing = false;
        this.onConnectCalled = false;
        this.sharedNotificationCenter = null;
        this.NotificationName = null;
        this.ComponentTreeConnector = null;
        this.sharedInterface = null;
        this.Module = null;
        this.element = null;
    }

    registerOnPreConnect(callback) {
        if(!callback)   return;
        this.preConnectCallbacks.addUnique(callback);
    }

    unregisterOnPreConnect(callback) {
        if(!callback)   return;
        this.preConnectCallbacks.remove(callback)
    }

    registerOnConnect(callback) {
        if(!callback)   return;
        this.connectCallbacks.addUnique(callback);
    }

    unregisterOnConnect(callback) {
        if(!callback)   return;
        this.connectCallbacks.remove(callback);
    }

    registerOnRelease(callback) {
        if(!callback)   return;
        this.releaseCallbacks.addUnique(callback);
    }

    unregisterOnRelease(callback) {
        if(!callback)   return;
        this.releaseCallbacks.remove(callback)
    }

    register(onConnect, onRelease)  {
        this.registerOnConnect(onConnect);
        this.registerOnRelease(onRelease);
    }

    unregister(onConnect, onRelease)  {
        this.unregisterOnConnect(onConnect);
        this.unregisterOnRelease(onRelease);
    }

    registerTargetChangeCallback(callback, target) {
        if(!callback)   return;
        this.targetChangeCallbacks.addUnique({target: target, fn: callback});
    }

    unregisterTargetChangeCallback(callback, target) {
        if(!callback)   return;
        this.connectCallbacks.remove({target: target, fn: callback});
    }

    callTargetChangeCallbacks(result)   {
        let target = typeof(result) === 'string' ? result : undefined;
        this.targetChangeCallbacks.forEach(e => {
            let result_ = target !== undefined ? new OEChangeTargetResult(OEAppInitResultCode.ok, typeof(e.target) === 'string' ? target !== e.target : false, target) : result;
            e.fn(result_, this);
        });
        this.targetChangeCallbacks.removeAll();
    }

    connect()    {
        if(!this.moduleId) {
            this.reset();
            return false;
        }

        let element = document.getElementById(this.moduleId);
        if(!element) {
            this.reset();
            return false;
        }

        this.element = element;
        let contentWindow = this.element.contentWindow;

        if(contentWindow.Module === undefined)    {
            this.reset();
            return false;
        }

        //
        this.sharedNotificationCenter = contentWindow.sharedNotificationCenter;
        this.NotificationName = contentWindow.NotificationName;
        this.ComponentTreeConnector = contentWindow.ComponentTreeConnector;
        this.sharedInterface = null;
        this.Module = contentWindow.Module;
        
        this.preReady = true;

        //
        if(!this.sharedNotificationCenter.isRegistered(this.NotificationName.initializationBegin, this.onInitializationBegin))  {
            this.sharedNotificationCenter.register(this.NotificationName.initializationBegin, this.onInitializationBegin);
        }

        if(!this.sharedNotificationCenter.isRegistered(this.NotificationName.readyForLicensingService, this.onReadyForLicensingService))  {
            this.sharedNotificationCenter.register(this.NotificationName.readyForLicensingService, this.onReadyForLicensingService);
        }

        if(!this.sharedNotificationCenter.isRegistered(this.NotificationName.initializationFinished, this.onInitializationFinished))  {
            this.sharedNotificationCenter.register(this.NotificationName.initializationFinished, this.onInitializationFinished);
        }

        //
        if(this.Module.statusManager.isInitializing() || this.Module.statusManager.isInitialized()) {
            this.onInitializationBegin();
        }

        if(this.Module.statusManager.isInitialized()) {
            this.onInitializationFinished();
        }

        return true;
    }

    connectLicensingModule()    {
        if(!slInterface.isPreReady())  return;

        //slInterface.printAnalytics(true);

        if(slInterface.isReady())  {

            if(!this.licensingServiceInjected)  {
                console.log('Injecting shared licensing module.');
                this.element.contentWindow.licensingModule = slInterface.Module;
                this.element.contentWindow.licensingService = slInterface.sharedInterface.getLicensingService();
                this.licensingServiceInjected = true;
            }

            if(this.readyForLicensingService && !this.licensingServiceConnected)   {
                this.Module.onLicensingServiceConnect();
                this.licensingServiceConnected = true;
            }

        } else {
            slInterface.register(this.onLicensingModuleConnected);
        }
    }

    onLicensingModuleConnected()  {

        if(!this.licensingServiceInjected)  {
            console.log('Injecting shared licensing module on licensing module connect.');
            this.element.contentWindow.licensingModule = slInterface.Module;
            this.element.contentWindow.licensingService = slInterface.sharedInterface.getLicensingService();
            this.licensingServiceInjected = true;
        }

        if(this.readyForLicensingService && !this.licensingServiceConnected)   {
            this.Module.onLicensingServiceConnect();
            this.licensingServiceConnected = true;
        }
        
        slInterface.unregister(this.onLicensingModuleConnected);
    }

    release()   {
        this.callTargetChangeCallbacks(new OEChangeTargetResult(OEAppInitResultCode.error, false, '', new OEAppStatusError(OEAppStatusErrorCode.unexpected)));

        if(slInterface.isPreReady()) slInterface.unregister(this.onLicensingModuleConnected);

        this.licensingServiceInjected = false;
        this.licensingServiceConnected = false;
        
        if(this.isReady())    {
            this.Module.onOEInterfaceRelease();
            this.releaseCallbacks.forEach((callback) => callback(this));
        }

        this.reset();
    }

    onInitializationBegin()    {
        console.log("Begin initialization...");
        
        if(this.ready)  {
            this.Module.onOEInterfaceRelease();
            this.releaseCallbacks.forEach((callback) => callback(this));
        }

        let contentWindow = this.element.contentWindow;

        //
        this.readyForLicensingService = false;
        this.ready = false;
        this.initializing = true;
        this.onConnectCalled = false;
        this.sharedInterface = contentWindow.sharedInterface;

        //
        //this.printAnalytics(true);

        //
        this.preConnectCallbacks.forEach((callback) => callback(this));
    }

    onReadyForLicensingService(message, userInfo) {
        this.readyForLicensingService = true;
        this.connectLicensingModule();
    }

    onInitializationFinished(message, userInfo) {
        let result = new OEChangeTargetResult(userInfo ? userInfo.result : this.sharedInterface.getInitializationResult());
        
        if(result.code == OEAppInitResultCode.aborted || result.code == OEAppInitResultCode.error)  {
            this.initializing = false;
            this.callTargetChangeCallbacks(result);
            return;
        }

        //
        this.ready = true;
        this.initializing = false;

        //
        if(!this.readyForLicensingService)  {
            this.readyForLicensingService = true;
            this.connectLicensingModule();
        }

        //
        this.Module.onOEInterfaceConnect();
        
        //
        this.connectCallbacks.forEach((callback) => callback(this));
        this.onConnectCalled = true;

        this.callTargetChangeCallbacks(result);

        console.log("Initialization finished with final heap size " + this.Module.HEAP8.length.toString());
    }

    updateModuleConfig(config)  {
        if(!this.isPreReady() || !config)    return;

        if(typeof(this.Module.config) !== 'object')   this.Module.config = {};
        
        this.Module.config.version = typeof(config.version) === 'string' ? config.version : undefined;
        this.Module.config.flags = Array.isArray(config.flags) ? clone(config.flags) : undefined;

        this.Module.config.labelsEnabled = !!config.labelsEnabled;
        this.Module.config.labelsLatin = !!config.labelsLatin;
        this.Module.config.noContextMenu = config.noContextMenu;

        this.Module.config.featureAccess = config.featureAccess ? clone(config.featureAccess) : undefined;

        this.Module.config.link = typeof(config.link) === 'string' ? config.link : undefined;
        this.Module.config.uiLink = typeof(config.uiLink) === 'string' ? config.uiLink : undefined;
        this.Module.config.linkRef = typeof(config.linkRef) === 'string' ? config.linkRef : undefined;
        this.Module.config.uiLinkRef = typeof(config.uiLinkRef) === 'string' ? config.uiLinkRef : undefined;

        this.Module.config.uiStateMC = config.uiStateMC ? clone(config.uiStateMC) : undefined;

        this.Module.config.urlQuery = typeof(config.urlQuery) === 'string' ? config.urlQuery : undefined;

        this.Module.config.loadFlags = typeof(config.loadFlags) === 'number' ? config.loadFlags : undefined;

        this.Module.config.license = typeof(config.license) === 'string' ? config.license : undefined;
    }

    changeTarget(nextTarget, config, callback)  {
        if(!this.isReady() && (!this.sharedInterface || this.sharedInterface.getInitializationResult().code !== this.Module.AppInitResultCode.aborted))  {
            let result = new OEChangeTargetResult(OEAppInitResultCode.error, false, '', new OEAppStatusError(OEAppStatusErrorCode.notReady));
            if(callback)    callback(result, this);
            this.callTargetChangeCallbacks(result);
            return false;
        }

        let fn = undefined;
        
        if(callback)    {
            this.registerTargetChangeCallback(callback, nextTarget);    // do it that way since this preserves the order of callback calls
            fn = function(result)   {
                let result_ = (new OEChangeTargetResult()).initWithResult(result);
                this.callTargetChangeCallbacks(result_);
            }.bind(this);
        }

        this.updateModuleConfig(config);

        return !!this.sharedInterface.getUIControllerApp().changeTarget(coreTargetTranslation(nextTarget), fn);
    }

    isPreReady()   {
        return this.preReady;
    }

    isReady()   {
        return this.ready;
    }

    isInitializing()   {
        return this.initializing;
    }

    isOnConnectCalled()   {
        return this.onConnectCalled;
    }

    startUp()   {
        if(this.element)    {
            let contentWindow = this.element.contentWindow;
            contentWindow.startUp();
        }
    }

    injectSentry()  {
        if(!global.Sentry || !this.isPreReady()) return false;
        this.element.contentWindow.Sentry = global.Sentry;
        return true;
    }

    injectAnalytics()  {
        if(!global.umami || !this.isPreReady()) return false;
        this.element.contentWindow.umami = global.umami;
        return true;
    }

    printCallbackListAnalytics()    {
        let numPreConnectCallbacks = this.preConnectCallbacks.size();
        let numConnectCallbacks = this.connectCallbacks.size();
        let numReleaseCallbacks = this.releaseCallbacks.size();
        console.log('oe-interface ' + (this.moduleId ? this.moduleId : '') + ' callbacks: ' + numPreConnectCallbacks + ', ' + numConnectCallbacks + ', ' + numReleaseCallbacks);
    }

    printAnalytics(extended)    {
        this.printCallbackListAnalytics();
        if(this.sharedNotificationCenter)   this.sharedNotificationCenter.printAnalytics(extended);
    }
};

class OEInterfaceManager {

    constructor() {
        this.interfaces = new Object();
    }

    getInterface(moduleId)   {
        if(typeof(moduleId) !== 'string')   return undefined;
        
        if(!(moduleId in this.interfaces)) {
            this.interfaces[moduleId] = new OEInterface(moduleId);
        }

        return this.interfaces[moduleId];
    }

    connect(moduleId) {
        let iface = this.getInterface(moduleId);
        return iface.connect();
    }

    release(moduleId)    {
        let iface = this.getInterface(moduleId);
        iface.release();
    }
};

export let oeInterfaceManager = new OEInterfaceManager();
